import { normalizeUkDigits } from './phone';

export const DIRECTORY_PHONE_DUPLICATE_MESSAGE =
  'A Directory customer already exists with this phone number.';

export type CustomerMobileRow = {
  id?: string;
  phone_number?: string | null;
  phone_normalized?: string | null;
};

export function customerMobileIdentity(phone?: string | null): string | null {
  return normalizeUkDigits(phone);
}

export function customerMobilePhones(row: CustomerMobileRow): Array<string | null> {
  return [row.phone_normalized ?? null, row.phone_number ?? null];
}

export function customerRowMatchesMobile(row: CustomerMobileRow, phone?: string | null): boolean {
  const needle = customerMobileIdentity(phone);
  if (!needle) return false;
  return customerMobilePhones(row).some((value) => customerMobileIdentity(value) === needle);
}

export function findCustomersByMobile<T extends CustomerMobileRow>(
  customers: T[],
  phone?: string | null,
): T[] {
  return customers.filter((customer) => customerRowMatchesMobile(customer, phone));
}

/** Partial unique index semantics: NULL / blank normalized values do not collide. */
export function wouldViolateNormalizedPhoneUnique(
  existingNormalized: Array<string | null | undefined>,
  incomingNormalized: string | null | undefined,
  options?: { excludeIndex?: number },
): boolean {
  if (!incomingNormalized) return false;
  return existingNormalized.some((value, index) => (
    index !== options?.excludeIndex
    && Boolean(value)
    && value === incomingNormalized
  ));
}

export function sharedLandlineAllowed(
  left: { mobile?: string | null; landline?: string | null },
  right: { mobile?: string | null; landline?: string | null },
): boolean {
  const sameLandline = Boolean(
    customerMobileIdentity(left.landline)
    && customerMobileIdentity(left.landline) === customerMobileIdentity(right.landline),
  );
  const sameMobile = Boolean(
    customerMobileIdentity(left.mobile)
    && customerMobileIdentity(left.mobile) === customerMobileIdentity(right.mobile),
  );
  return sameLandline && !sameMobile;
}

function errorText(error: unknown): string {
  if (error instanceof Error) return `${error.message} ${error.name}`;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    const record = error as { message?: unknown; code?: unknown; details?: unknown; hint?: unknown };
    return [record.message, record.code, record.details, record.hint].filter(Boolean).join(' ');
  }
  return String(error || '');
}

export function isDirectoryPhoneDuplicateError(error: unknown): boolean {
  const text = errorText(error).toLowerCase();
  const unique = text.includes('23505')
    || text.includes('duplicate key')
    || text.includes('unique constraint')
    || text.includes('already exists');
  if (!unique) return false;
  return text.includes('phone_normalized')
    || text.includes('idx_customers_phone_normalized')
    || text.includes('idx_customers_phone_number')
    || text.includes('phone number');
}

export function directoryConstraintError(error: unknown): Error | null {
  if (isDirectoryPhoneDuplicateError(error)) {
    return new Error(DIRECTORY_PHONE_DUPLICATE_MESSAGE);
  }
  return null;
}

export function throwDirectoryConstraintError(error: unknown): never {
  throw directoryConstraintError(error) || (error instanceof Error ? error : new Error(errorText(error)));
}

export function isMissingPhoneNormalizedColumn(error: unknown): boolean {
  const text = errorText(error).toLowerCase();
  return text.includes('phone_normalized')
    && (text.includes('42703') || text.includes('does not exist') || text.includes('schema cache'));
}
